import * as _ from "lodash";
import * as angular from "angular";
import { AdminDataProvider } from "../../../data-provider/admin.dp";
import { Campaign } from "../../../models/campaign.model";
import { AdminListSort } from "./admin-list-sort";
import * as activity from "./admin-activity.helper";
import { AdminCampaignService } from "../../../services/admin-campaign.service";
import { CampaignService } from "../../../services/campaign.service";
import { UserService } from "../../../services/users.service";
import { CredentialsManager } from "../../../shared/credentials-manager";
import { DialogUtils } from "../../../helper/dialog.helper";
import { ToastUtils } from "../../../helper/toast.helper";
import { FilterDataProvider } from "../../../data-provider/filter.dp";
import { Filter } from "../../../models/filter.model";
import { CampaignDataProvider } from "../../../data-provider/campaign.dp";
import { NavigationBarHelperService } from "../../navigation/navigation-bar.helper";
import { XeroService } from "../../../services/xero.service";

let AdminCampaignsListComponent = {
    selector: "vcAdminCampaignsList",
    templateUrl:
        "/angular/components/admin/lists/campaigns-list.component.html",
    bindings: {},
    controller: class AdminCampaignsListController {
        // * bindings for typescript compiler *
        // ************************************

        /** Placeholder rows while the first page loads, so the table keeps its height. */

        public skeleton = new Array(8);

        /**
         * Header sorting (AV-17963). Contacts reuses the existing "x of y"
         * column rather than repeating the same number in a second one.
         */
        public sorting = new AdminListSort(
            {
                campaignid: "id",
                houseoccupantslimit: "contacts",
                houses: "houses",
                interactions: "interactions",
                // Activity lens columns (AV-17958)
                a_interactions: "interactions",
                a_logins: "logins",
                a_activeusers: "activeusers",
                a_contacts: "contacts",
                a_lastactivity: "last_active",
                a_trend: "trend",
            },
            () => this.filter
        );
        private allColumns: any;

        private loading = false;
        private allLoaded = false;
        /**
         * Where the next page starts, from server pages only: rows put back
         * on unpin must not move it (a pin from far down the list would
         * otherwise skip everything in between).
         */
        private serverRows = 0;
        private serverCursor: any = null;
        private campaigns: Campaign[] = [];
        private searchCanceller: any;
        private kinds: any[] = [];
        public columns: object[];
        public filter: Filter;

        private $scope: any;
        private $q: any;
        private toast: ToastUtils;
        private dialog: DialogUtils;
        public __env: any;
        public dataProvider: AdminDataProvider;

        private navigationBarService: NavigationBarHelperService;
        private credentialsManager: CredentialsManager;
        private service: AdminCampaignService;
        private userService: UserService;
        private campaignService: CampaignService;
        private campaignDataProvider: CampaignDataProvider;
        private firstLoad = true;
        public searchDelay: any;
        private readonly $interval: any;

        private readonly $filter: any;

        /** True while a join-and-open is in flight; disables every row link. */
        public entering = false;
        private readonly $cookies: any;

        /** Pinned accounts, fetched separately so they sit above the page. */
        public pinned: Campaign[] = [];
        /**
         * Bumped by every pinned-rows load and every pin/unpin; a response
         * for an older generation is dropped, so a slow request under an
         * old search (or from before an unpin) can't bring rows back.
         */
        private pinnedRequest = 0;
        /** Row selection for the bulk "remove me" action (campaignid -> true). */
        public selected: { [campaignid: string]: boolean } = {};
        public removing = false;

        constructor(
            $scope: any,
            $cookies: any,
            AdminCampaignService: AdminCampaignService,
            AdminDataProvider: AdminDataProvider,
            UserService: UserService,
            CampaignService: CampaignService,
            CredentialsManager: CredentialsManager,
            FilterDataProvider: FilterDataProvider,
            CampaignDataProvider: CampaignDataProvider,
            NavigationBarHelperService: NavigationBarHelperService,
            ToastUtils: ToastUtils,
            DialogUtils: DialogUtils,
            __env: any,
            $q: any,
            $filter: any,
            $interval: any
        ) {
            this.$scope = $scope;
            this.$cookies = $cookies;
            this.service = AdminCampaignService;
            this.dataProvider = AdminDataProvider;
            this.userService = UserService;
            this.campaignService = CampaignService;
            this.credentialsManager = CredentialsManager;
            this.navigationBarService = NavigationBarHelperService;
            this.toast = ToastUtils;
            this.dialog = DialogUtils;
            this.$interval = $interval;
            this.__env = __env;
            this.$q = $q;
            this.filter = FilterDataProvider.appliedFilter;
            this.allColumns = this.dataProvider.getColumns();
            this.campaignDataProvider = CampaignDataProvider;
            this.columns = this.allColumns.campaigns;
            this.$filter = $filter;
        }

        $onInit() {
            // AV-17958: the lens picks the column set.
            this.$scope.$watch(
                () => this.filter && this.filter.lens,
                (lens: string | undefined) => {
                    this.columns =
                        this.allColumns[
                            AdminDataProvider.columnsKey("campaigns", lens)
                        ] || this.allColumns.campaigns;
                }
            );
            this.$scope.$watch(
                () => this.filter,
                (newValue: object, oldValue: object) => {
                    if (oldValue && newValue !== oldValue) {
                        if (this.searchDelay) {
                            this.searchDelay = this.$interval.cancel(
                                this.searchDelay
                            );
                        }
                        this.searchDelay = this.$interval(() => {
                            this.$interval.cancel(this.searchDelay);
                            if (
                                this.firstLoad &&
                                Object.keys(this.filter.nonEmptyValues()).length
                            ) {
                                this.navigationBarService.setFilterBarOpen(
                                    true
                                );
                            }
                            if (this.searchCanceller) {
                                this.searchCanceller.resolve("User cancelled");
                            }
                            this.searchCanceller = this.$q.defer();
                            this.loadCampaigns(true);
                            this.firstLoad = false;
                        }, 500);
                    } else {
                        if (Object.keys(this.filter).length) {
                            this.loadCampaigns(true);
                            this.firstLoad = false;
                        }
                    }
                },
                true
            );
            this.campaignDataProvider
                .getKinds()
                .then((kinds) => (this.kinds = kinds));
        }

        /**
         * Built here rather than in the template: interpolation inside
         * md-tooltip keeps the source line breaks and the tip renders ragged.
         */
        homeAccountHint(): string {
            const region =
                this.__env.region == "usa"
                    ? "US"
                    : String(this.__env.region || "").toUpperCase();
            return (
                "Admin account (" +
                region +
                " home): admins always stay attached, so you can't leave it"
            );
        }

        statNumber(campaign: Campaign, key: string): string {
            return AdminListSort.statNumber(campaign, key);
        }

        // ---------- Activity lens (AV-17958) ----------

        activity(): boolean {
            return !!this.filter && this.filter.lens == "activity";
        }

        private activityParams(): {
            lens?: string;
            range?: number;
            trend?: string;
        } | null {
            // Only the Activity lens needs a range and a trend.
            if (!this.activity()) {
                return null;
            }
            return {
                lens: "activity",
                range: this.filter.range || 7,
                trend: this.filter.trend,
            };
        }

        /** Range-labelled header for the two windowed metrics. */
        columnLabel(column: { id: string; name: string }): string {
            if (column.id == "a_interactions" || column.id == "a_logins") {
                return (
                    column.name + " · " + activity.rangeLabel(this.filter.range)
                );
            }
            return column.name;
        }

        emptyText(): string {
            return this.activity()
                ? activity.emptyCopy(
                      this.filter.trend,
                      "accounts",
                      this.filter.range
                  )
                : "Nothing matches these filters.";
        }

        deltaText = activity.deltaText;
        deltaClass = activity.deltaClass;
        relativeDays = activity.relativeDays;
        lastActivityClass = activity.lastActivityClass;
        shortDate = activity.shortDate;
        trendKey = activity.trendKey;
        trendLabel = activity.trendLabel;
        trendIcon = activity.trendIcon;
        sparkPoints = activity.sparkPoints;
        sparkLast = activity.sparkLast;

        openActivityDialog(campaign: Campaign, $event: any): void {
            this.dialog.show({
                component: "vc-admin-activity-dialog",
                targetEvent: $event,
                size: "large",
                locals: {
                    kind: "campaign",
                    recordId: campaign.campaignid,
                    range: this.filter.range || 7,
                },
            });
        }

        /** Increments per counts request; a late response is ignored. */
        private chipCountsRequest = 0;
        /** The filters the current chip counts answer for. */
        private chipCountsKey: string | null = null;

        /**
         * One page of rows under the current filters and order. The Activity
         * lens draws only id, name and organisation, so it takes the lean
         * /v2 list; the Directory takes admin_all.
         */
        private fetchRows(
            offset: any,
            timeout?: any,
            exportAll = false,
            filter: any = this.filter.nonEmptyValues()
        ): Promise<object> {
            if (this.activity() && !exportAll) {
                return this.service.activity(
                    filter,
                    offset,
                    timeout,
                    this.filter.attached,
                    this.filter.thisRegion,
                    this.sorting.sort,
                    this.sorting.direction,
                    this.activityParams()
                );
            }
            return this.service.all(
                filter,
                offset,
                timeout,
                this.filter.attached,
                this.filter.thisRegion,
                exportAll,
                this.sorting.sort,
                this.sorting.direction,
                this.activityParams()
            );
        }

        loadCampaigns(reload: boolean): void {
            if (reload || !this.campaigns || this.campaigns.length == 0) {
                if (reload) this.allLoaded = false;
                this.loading = true;
                this.loadChipCounts();
                this.fetchRows(
                    null,
                    this.searchCanceller
                        ? this.searchCanceller.promise
                        : undefined
                ).then((response: object) => {
                    const json = response["data"];
                    this.loading = false;
                    this.campaigns = json.data;
                    this.trackServerPage(json.data || [], true);
                    // A short first page is the whole result set; the
                    // server pages 50 at a time.
                    this.allLoaded = !json.data || json.data.length < 50;
                    this.selected = {};
                    this.loadPinned();
                    this.loadActivityStats(this.campaigns || []);
                });
            }
        }

        /**
         * Activity lens cells are filled after the rows land (AV-17958):
         * admin_all orders by the nightly aggregate but no longer carries
         * it, so the page renders first and the numbers follow. Rows are
         * matched by id, so a response for rows that have since gone is
         * harmless; one for a different range is dropped.
         */
        private loadActivityStats(rows: Campaign[], pinned = false): void {
            if (!this.activity() || !rows.length) {
                return;
            }
            const range = this.filter.range || 7;
            const ids = rows.map((row) => row.campaignid);
            this.service.activityStats(ids, range).then((response: object) => {
                if (!this.activity() || (this.filter.range || 7) != range) {
                    return;
                }
                const stats = (response["data"] && response["data"].data) || {};
                const target = pinned ? this.pinned : this.campaigns;
                angular.forEach(target || [], (campaign: any) => {
                    if (stats[campaign.campaignid]) {
                        campaign.activity = stats[campaign.campaignid];
                    }
                });
            });
        }

        /**
         * What the chip counts depend on. Sorting is left out on purpose: the
         * order of the rows never changes how many there are, so a header
         * click must not cost a second counting query.
         */
        private chipCountsKeyFor(): string {
            // The chip fields themselves are left out: the counts ignore the
            // status chip, and in the Activity lens they come back as a
            // status x trend grid that covers every chip, so clicking a chip
            // never needs a new count. The range stays in (it changes the
            // trends).
            const values: any = angular.copy(this.filter.nonEmptyValues());
            delete values.status;
            const activity: any = angular.copy(this.activityParams());
            if (activity) delete activity.trend;
            return angular.toJson([
                values,
                !!this.filter.attached,
                // The region itself, not just whether one is picked:
                // EU only -> US only must count again.
                this.filter.thisRegion || null,
                activity,
            ]);
        }

        /**
         * The numbers on the quick-filter chips, fetched in parallel with the
         * rows so the list never waits on the counting query. Only refetched
         * when the filters change; a response that arrives after they have
         * moved on again is dropped, so a slow count can never label the
         * wrong list.
         */
        private loadChipCounts(): void {
            const key = this.chipCountsKeyFor();
            if (key === this.chipCountsKey) {
                return;
            }
            this.chipCountsKey = key;
            // Drop the stale numbers rather than show the last filter's
            // counts beside this filter's rows.
            delete this.dataProvider.chipCounts.campaigns;
            delete this.dataProvider.chipCounts["campaigns:grid"];
            const token = ++this.chipCountsRequest;
            this.service
                .chipCounts(
                    this.filter.nonEmptyValues(),
                    this.filter.attached,
                    this.filter.thisRegion,
                    this.activityParams()
                )
                .then((response: object) => {
                    if (token !== this.chipCountsRequest) {
                        return;
                    }
                    const json = response["data"];
                    if (json && json.data && json.data.grid) {
                        // Activity lens: status x trend; the chips sum it.
                        this.dataProvider.chipCounts["campaigns:grid"] =
                            json.data.grid;
                    } else if (json && json.data) {
                        this.dataProvider.chipCounts.campaigns = json.data;
                    }
                });
        }

        // ---------- pinned rows ----------

        /**
         * Pinned ids live in localStorage; the rows may not be on the loaded
         * page, so ask the same endpoint for exactly those ids under the same
         * filters. A pinned Trial account still disappears under "Paid".
         */
        private loadPinned(): void {
            this.dataProvider.ready.then((): void => this.loadPinnedRows());
        }

        private loadPinnedRows(): void {
            const request = ++this.pinnedRequest;
            const ids = this.dataProvider.getPins("campaigns");
            if (!ids.length) {
                this.pinned = [];
                return;
            }
            const filter: any = angular.extend(
                {},
                this.filter.nonEmptyValues(),
                {
                    ids: ids,
                }
            );
            this.fetchRows(null, undefined, false, filter).then(
                (response: object) => {
                    if (request !== this.pinnedRequest) return;
                    const json = response["data"];
                    const order = ids.slice();
                    this.pinned = (json.data || [])
                        .filter((c: Campaign) => this.isPinned(c))
                        .sort(
                            (a: Campaign, b: Campaign) =>
                                order.indexOf(Number(a.campaignid)) -
                                order.indexOf(Number(b.campaignid))
                        );
                    this.loadActivityStats(this.pinned, true);
                }
            );
        }

        isPinned(campaign: Campaign): boolean {
            return this.dataProvider.isPinned("campaigns", campaign.campaignid);
        }

        togglePin(campaign: Campaign): void {
            const state = this.dataProvider.togglePin(
                "campaigns",
                campaign.campaignid
            );
            // Pins changed: any pinned-rows response still in flight is stale.
            this.pinnedRequest++;
            if (state === "limit") {
                this.toast.error(
                    "You can pin up to " +
                        AdminDataProvider.PIN_LIMIT +
                        " accounts"
                );
                return;
            }
            if (state === "pinned") {
                this.pinned = this.pinned.concat([campaign]);
            } else {
                this.pinned = this.pinned.filter(
                    (c: Campaign) => c.campaignid != campaign.campaignid
                );
                // Put the row back where the keyset order would have it, but
                // only if the pages loaded so far reach it; otherwise it
                // arrives with its own page.
                if (
                    !this.campaigns.some(
                        (c: Campaign) => c.campaignid == campaign.campaignid
                    ) &&
                    !this.sorting.sort &&
                    !this.activity() &&
                    (this.allLoaded ||
                        Number(campaign.campaignid) > Number(this.serverCursor))
                ) {
                    this.campaigns = this.campaigns
                        .concat([campaign])
                        .sort(
                            (a: Campaign, b: Campaign) =>
                                Number(b.campaignid) - Number(a.campaignid)
                        );
                }
            }
        }

        private trackServerPage(rows: Campaign[], first: boolean): void {
            this.serverRows = (first ? 0 : this.serverRows) + rows.length;
            if (rows.length) {
                this.serverCursor = rows[rows.length - 1].campaignid;
            } else if (first) {
                this.serverCursor = null;
            }
        }

        /** Pinned first, then the loaded page minus anything pinned. */
        displayRows(): Campaign[] {
            if (!this.pinned.length) return this.campaigns || [];
            const pinnedIds = this.pinned.map((c: Campaign) =>
                String(c.campaignid)
            );
            return this.pinned.concat(
                (this.campaigns || []).filter(
                    (c: Campaign) =>
                        pinnedIds.indexOf(String(c.campaignid)) === -1
                )
            );
        }

        isLastPinned(campaign: Campaign): boolean {
            return (
                this.pinned.length > 0 &&
                this.pinned[this.pinned.length - 1].campaignid ==
                    campaign.campaignid
            );
        }

        // ---------- row selection: bulk copy anywhere, bulk "remove me" when attached ----------

        /**
         * "Select all matching": the header checkbox only reaches the rows
         * already loaded, so once those are all ticked the bar offers the
         * whole filtered result set. Copy then fetches every matching row.
         */
        public selectAllMatching = false;
        public copying = false;

        toggleSelect(campaign: Campaign): void {
            this.selectAllMatching = false;
            const key = String(campaign.campaignid);
            if (this.selected[key]) delete this.selected[key];
            else this.selected[key] = true;
        }

        /** Offer the whole result set once every loaded row is ticked and more exist. */
        canSelectAllMatching(): boolean {
            return (
                !this.selectAllMatching &&
                !this.allLoaded &&
                this.allLoadedSelected()
            );
        }

        chooseAllMatching(): void {
            this.selectAllMatching = true;
        }

        selectedCount(): number {
            return Object.keys(this.selected).length;
        }

        /** Rows the admin can actually leave: joined, and not the region's home account. */
        leaveable(campaign: Campaign): boolean {
            return (
                (campaign.member == 1 || campaign.member == "1") &&
                !campaign.adminhome
            );
        }

        /** Every loaded row can be selected; copying needs no membership. */
        selectable(campaign: Campaign): boolean {
            return !!campaign && !!campaign.campaignid;
        }

        selectedRows(): Campaign[] {
            return this.displayRows().filter(
                (c: Campaign) => !!this.selected[String(c.campaignid)]
            );
        }

        /** How many of the selected rows the admin could leave. */
        removableSelectedCount(): number {
            return this.selectedRows().filter((c: Campaign) =>
                this.leaveable(c)
            ).length;
        }

        allLoadedSelected(): boolean {
            const rows = this.displayRows();
            return (
                rows.length > 0 &&
                rows.every(
                    (c: Campaign) => !!this.selected[String(c.campaignid)]
                )
            );
        }

        toggleSelectAll(): void {
            this.selectAllMatching = false;
            if (this.allLoadedSelected()) {
                this.selected = {};
                return;
            }
            const next: { [id: string]: boolean } = {};
            this.displayRows().forEach((c: Campaign) => {
                next[String(c.campaignid)] = true;
            });
            this.selected = next;
        }

        clearSelection(): void {
            this.selected = {};
            this.selectAllMatching = false;
        }

        /**
         * Removes the admin from every selected account, one call at a time
         * (the endpoint is per account), then reloads the list so the rows
         * reflect the server.
         */
        removeSelected($event: any): void {
            const ids = this.selectedRows()
                .filter((c: Campaign) => this.leaveable(c))
                .map((c: Campaign) => Number(c.campaignid));
            if (!ids.length || this.removing) return;
            this.dialog
                .delete({
                    targetEvent: $event,
                    locals: {
                        title:
                            "Remove yourself from " +
                            ids.length +
                            " account" +
                            (ids.length == 1 ? "" : "s") +
                            "?",
                        bodyText:
                            "<p>You will lose access to the selected accounts until you add yourself again.</p>",
                    },
                })
                .then(() => {
                    this.removing = true;
                    this.userService.bulk_self_remove(ids).then(
                        (response: object) => {
                            const json = response["data"];
                            this.removing = false;
                            this.selected = {};
                            if (
                                json &&
                                json.campaign &&
                                json.campaign.campaignid
                            ) {
                                this.credentialsManager.reauth(
                                    json.campaign.campaignid
                                );
                            }
                            const removed = (json && json.removed) || 0;
                            const failed = (json && json.failed) || 0;
                            const kept = (json && json.kept) || 0;
                            const summary =
                                "Removed from " +
                                removed +
                                " account" +
                                (removed == 1 ? "" : "s") +
                                (kept ? ", kept your home account" : "") +
                                (failed ? ", " + failed + " failed" : "");
                            if (failed) this.toast.error(summary);
                            else this.toast.success(summary);
                            // Membership moved, so the counts are stale
                            // even though the filters are not.
                            this.chipCountsKey = null;
                            this.loadCampaigns(true);
                        },
                        () => {
                            this.removing = false;
                            this.toast.error(
                                "Could not remove you from the selected accounts"
                            );
                        }
                    );
                });
        }

        scrollLoad(): void {
            if (!this.loading && !this.allLoaded) {
                this.loading = true;
                this.fetchRows(
                    // Sorted pages and the Activity lens walk by row
                    // count; the default order keeps walking back
                    // through campaignid.
                    this.sorting.sort || this.activity()
                        ? this.serverRows
                        : this.serverCursor
                ).then((response: object) => {
                    const json = response["data"];
                    if (json.data.length) {
                        this.trackServerPage(json.data, false);
                        this.campaigns = this.campaigns.concat(
                            json.data.filter(
                                (row: Campaign) =>
                                    !this.campaigns.some(
                                        (c: Campaign) =>
                                            c.campaignid == row.campaignid
                                    )
                            )
                        );
                        this.loadActivityStats(json.data);
                    } else {
                        this.allLoaded = true;
                    }
                    this.loading = false;
                });
            }
        }

        /**
         * Joins the current admin to the account (no-op when already a
         * member), switches the session into it and opens the dashboard.
         */
        joinAndOpenAccount(campaign: Campaign): void {
            if (this.entering) return;
            this.entering = true;
            const open = (): void => this.switchAndOpen(campaign, "/app.php#/");
            if (campaign.member == 1 || campaign.member == "1") {
                open();
                return;
            }
            this.userService.add({ campaignid: campaign.campaignid }).then(
                (): void => {
                    campaign.member = 1;
                    open();
                },
                // May already be a member — try to switch regardless
                (): void => open()
            );
        }

        private switchAndOpen(campaign: Campaign, destination: string): void {
            this.campaignService.live({ campaignid: campaign.campaignid }).then(
                (response: any): void => {
                    const json: any = response.data;
                    if (json.success) {
                        // Matches changeLiveCampaign: the destination
                        // app re-runs its first-login bootstrap
                        this.$cookies.remove("signedIn");
                        this.credentialsManager
                            .reauth(
                                json.campaign.campaignid,
                                json.campaign.oid || null
                            )
                            .then((): void => {
                                window.location.href = destination;
                            });
                    } else if (json.region && json.handoff) {
                        // Hosted in the other region: the API minted a
                        // single-use token and has already dropped our
                        // session here, so carry it over rather than stop.
                        this.credentialsManager.handoff(
                            json.region,
                            json.handoff,
                            destination
                        );
                    } else if (json.region) {
                        this.entering = false;
                        this.toast.error(
                            "This account is hosted in the '" +
                                json.region +
                                "' region — open it from that region's admin panel"
                        );
                    } else {
                        this.entering = false;
                        this.toast.error(
                            json.message || "Could not switch into the account"
                        );
                    }
                },
                (): void => {
                    this.entering = false;
                    this.toast.error("Could not switch into the account");
                }
            );
        }

        addToCampaign(campaign: Campaign): void {
            this.userService
                .add({ campaignid: campaign.campaignid })
                .then((response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        campaign.member = 1;
                        this.toast.success("Added to Account");
                    } else {
                        this.toast.error(
                            "Failed to add you to account: " +
                                campaign.campaignname
                        );
                    }
                });
        }

        removeFromCampaign(campaign: Campaign): void {
            this.userService
                .self_remove({ id: campaign.campaignid })
                .then((response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        if (json.campaign && json.campaign.campaignid) {
                            this.credentialsManager.reauth(
                                json.campaign.campaignid
                            );
                        }
                        campaign.member = 0;
                        this.toast.success("Removed From Account");
                    } else if (json.region && json.handoff) {
                        // Our replacement live campaign is hosted in the
                        // other region - carry the session over rather than
                        // being signed out of both.
                        this.credentialsManager.handoff(
                            json.region,
                            json.handoff,
                            "/admin.php/#/admin_campaigns"
                        );
                    } else {
                        this.toast.error(json.message);
                    }
                });
        }

        reactivateCampaign(campaign: Campaign, $event: any): void {
            this.dialog
                .delete({
                    targetEvent: $event,
                    locals: {
                        bodyText:
                            "<p>Are you sure you want to reactivate this campaign and create a new subscription for it?</p>",
                        title: "Reactivate " + campaign.campaignname + "?",
                    },
                })
                .then(() => {
                    this.service
                        .reactivate(campaign)
                        .then((response: object) => {
                            const json = response["data"];

                            if (json.success) {
                                this.toast.success("Account Reactivated");
                                campaign.campaignstatusname = "Paid";
                                campaign.campaignstatusid = "1";
                                campaign.subscriptionid =
                                    json.data.subscriptionid;
                            } else {
                                this.toast.error(
                                    "Campaign reactivate failed, Please view the network console to see what went wrong with stripe."
                                );
                            }
                        });
                });
        }

        enableExport(campaign: Campaign): void {
            this.service
                .enableExport({
                    id: campaign.campaignid,
                })
                .then((response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        this.toast.success(
                            "Export enabled for account " +
                                campaign.campaignname
                        );
                    } else {
                        this.toast.error(
                            "Something went wrong: Export not enabled for campaign " +
                                campaign.campaignname +
                                ". " +
                                (json.message ? json.message : "")
                        );
                    }
                });
        }

        openCampaignSubscriptionDialog(campaign: Campaign, $event: any): void {
            if (campaign.subscriptionid) {
                this.dialog.show({
                    component: "vc-campaign-subscription-dialog",
                    targetEvent: $event,
                    scope: this.$scope,
                    size: "x-large",
                    locals: {
                        campaign: campaign,
                    },
                });
            }
        }

        inFuture(dateTime: string): boolean {
            const expire = new Date(dateTime);
            const today = new Date();

            return expire > today;
        }

        openCampaignModal(
            campaign: Campaign,
            $event: any,
            section?: string
        ): void {
            this.dialog
                .show({
                    component: "vc-admin-edit-campaign",
                    targetEvent: $event,
                    scope: this.$scope,
                    size: "large",
                    locals: {
                        campaign: campaign,
                        initialSection: section || null,
                    },
                })
                .then((editedCampaign: object) => {
                    _.merge(campaign, editedCampaign);
                });
        }

        xeroLabel(status: string | null | undefined): string {
            return XeroService.label(status);
        }

        xeroUrl(id: string): string {
            return XeroService.contactUrl(id);
        }

        xeroStanding(row: any): { label: string; cls: string } {
            return XeroService.standing(row);
        }

        /** Xero invoice history, the Stripe charge-history equivalent. */
        openXeroInvoices(campaign: Campaign, $event: any): void {
            if (campaign.xero_status != "matched") return;
            this.dialog.show({
                component: "vc-xero-invoices-dialog",
                targetEvent: $event,
                scope: this.$scope,
                size: "x-large",
                locals: {
                    target: campaign.oid
                        ? { organisation: campaign.oid }
                        : { account: campaign.campaignid },
                },
            });
        }

        /** Stripe and Xero are exclusive: a subscription id means Stripe wins. */
        hasStripe(campaign: Campaign): boolean {
            const id = campaign && campaign.subscriptionid;
            return !!id && id != "none";
        }

        enableAPI(campaign: Campaign): void {
            this.service
                .enableAPI({
                    id: campaign.campaignid,
                })
                .then((response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        this.toast.success(
                            "API enabled for account " + campaign.campaignname
                        );
                    } else {
                        this.toast.error(
                            "Something went wrong: API not enabled for campaign " +
                                campaign.campaignname +
                                ". " +
                                (json.message ? json.message : "")
                        );
                    }
                });
        }

        openProspectorDialog(campaign: Campaign, $event: any): void {
            this.dialog.show({
                component: "vc-admin-prospector-dialog",
                targetEvent: $event,
                scope: this.$scope,
                size: "small",
                locals: {
                    campaign: campaign,
                },
            });
        }

        toggleReview(campaign: Campaign): void {
            this.service
                .toggleReview({
                    id: campaign.campaignid,
                })
                .then((response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        this.toast.success(
                            "Review disabled for account " +
                                campaign.campaignname
                        );
                    } else {
                        this.toast.error(
                            "Something went wrong: API not enabled for campaign " +
                                campaign.campaignname +
                                ". " +
                                (json.message ? json.message : "")
                        );
                    }
                });
        }

        geocode(campaign: Campaign, $event: any): void {
            this.dialog
                .delete({
                    targetEvent: $event,
                    locals: {
                        bodyText:
                            "<p>Geocoding costs can be quite high for large accounts and shouldn't be run by admins unless necessary.</p> " +
                            "<p>Are you sure you want to geocode houses for this campaign?</p>",
                        title: "Start Geocoding",
                    },
                })
                .then(() => {
                    this.service
                        .geocode({
                            id: campaign.campaignid,
                            allhouses: true,
                        })
                        .then((response: object) => {
                            const json = response["data"];
                            if (json.success) {
                                this.toast.success(
                                    "Geocoding started for account " +
                                        campaign.campaignname
                                );
                            } else {
                                this.toast.error(json.message);
                            }
                        });
                });
        }

        cancelCampaign(campaign: Campaign, $event: any): void {
            this.dialog
                .delete({
                    targetEvent: $event,
                    locals: {
                        bodyText:
                            "Are you sure you want to cancel this campaign?",
                        title: "Cancel Campaign",
                    },
                })
                .then(() => {
                    this.campaignService
                        .cancelCampaigns({
                            campaigns: [campaign],
                            feedback: "",
                            reason: "Admin cancelled",
                        })
                        .then((response: object) => {
                            const json = response["data"];
                            if (json.success) {
                                this.toast.success("Account Cancelled");
                                campaign.campaignstatusid = "3";
                                campaign.campaignstatusname = "Cancelled";
                            } else {
                                this.toast.error(
                                    "Campaign Cancellation failed, Please view the network console to see what went wrong with stripe."
                                );
                            }
                        });
                });
        }

        setTrialLength(campaign: Campaign, no: number): void {
            const expireOn = new Date();
            expireOn.setDate(expireOn.getDate() + no * 7);

            let dd: string | number = expireOn.getDate();
            if (dd < 10) dd = "0" + dd;

            let mm: string | number = expireOn.getMonth() + 1;
            if (mm < 10) mm = "0" + mm;
            const y = expireOn.getFullYear();

            const expireOnFormatted = y + "-" + mm + "-" + dd + " 00:00:00";

            this.userService
                .extend_trial({
                    campaignid: campaign.campaignid,
                    campaignexpire: expireOnFormatted,
                })
                .then((response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        campaign.campaignexpire = expireOnFormatted;
                        this.toast.success(
                            "Account Extended\n" +
                                "New expiry: " +
                                expireOnFormatted
                        );
                    } else {
                        this.toast.error(
                            "Trial Extension Failed, Please view the network console to see what went wrong with stripe."
                        );
                    }
                });
        }

        /**
         * One account as the tab-separated row the support sheet expects. The
         * blank cells are deliberate: they line up with columns the sheet
         * fills in by hand. Bulk copy emits one of these per line.
         */
        detailsRow(campaign: any): string {
            return (
                campaign.campaignid +
                "\t" +
                this.$filter("date")(
                    new Date(campaign.campaigncreationdatetime),
                    "dd/MM/yyyy"
                ) +
                "\t" +
                new Date(campaign.campaigncreationdatetime).toLocaleString(
                    "default",
                    { month: "short" }
                ) +
                "\t\t" +
                (campaign.plan && campaign.plan.cpname
                    ? campaign.plan.cpname
                    : "") +
                "\t" +
                campaign.campaignname +
                "\t" +
                (
                    (campaign.creator_ufname || "") +
                    " " +
                    (campaign.creator_ulname || "")
                ).trim() +
                "\t" +
                (campaign.owner_uemail || "") +
                "\t\t" +
                String(campaign.campaignregion || "").toUpperCase() +
                "\t\t" +
                "TBD\t" +
                this.getKindString(campaign.campaignkind, "category") +
                "\t" +
                this.getKindString(campaign.campaignkind, "subcategory")
            );
        }

        copyDetails(campaign: any): void {
            this.copyText(this.detailsRow(campaign));
            this.toast.success("Copied 1 account");
        }

        /**
         * Every selected row, one per line, ready to paste into a sheet,
         * oldest account (lowest campaign id) first.
         */
        copySelected(): void {
            if (this.selectAllMatching) {
                this.copyAllMatching();
                return;
            }
            const rows = this.selectedRows();
            if (!rows.length) return;
            this.copyText(this.detailsRows(rows));
            this.toast.success(
                "Copied " +
                    rows.length +
                    (rows.length == 1 ? " account" : " accounts")
            );
        }

        /**
         * Whole filtered result set, fetched through the same unpaged path
         * the export uses so the rows match what the list would show.
         */
        private copyAllMatching(): void {
            if (this.copying) return;
            this.copying = true;
            this.service
                .all(
                    this.filter.nonEmptyValues(),
                    null,
                    undefined,
                    this.filter.attached,
                    this.filter.thisRegion,
                    true,
                    this.sorting.sort,
                    this.sorting.direction,
                    this.activityParams()
                )
                .then(
                    (response: any): void => {
                        this.copying = false;
                        const rows: Campaign[] =
                            (response.data && response.data.data) || [];
                        if (!rows.length) {
                            this.toast.error(
                                "No accounts match the current filters"
                            );
                            return;
                        }
                        this.copyText(this.detailsRows(rows));
                        this.toast.success(
                            "Copied " + rows.length + " accounts"
                        );
                    },
                    (): void => {
                        this.copying = false;
                        this.toast.error(
                            "Could not fetch the matching accounts"
                        );
                    }
                );
        }

        /**
         * Copied rows, one per line, by campaign id ascending: the same order
         * whatever the list's sort, pins, or the order rows were ticked in.
         */
        private detailsRows(rows: Campaign[]): string {
            return rows
                .slice()
                .sort(
                    (a: Campaign, b: Campaign) =>
                        Number(a.campaignid) - Number(b.campaignid)
                )
                .map((c: Campaign) => this.detailsRow(c))
                .join("\n");
        }

        private copyText(text: string): void {
            const nav: any = window.navigator;
            if (nav.clipboard && nav.clipboard.writeText) {
                nav.clipboard
                    .writeText(text)
                    .catch((): void => this.copyLegacy(text));
                return;
            }
            this.copyLegacy(text);
        }

        private copyLegacy(text: string): void {
            const copyElement = document.createElement("textarea");
            copyElement.style.position = "fixed";
            copyElement.style.opacity = "0";
            copyElement.textContent = text;
            const body = document.getElementsByTagName("body")[0];
            body.appendChild(copyElement);
            copyElement.select();
            document.execCommand("copy");
            body.removeChild(copyElement);
        }

        getKindString(id: string, attribute: string): string {
            for (var i = 0; i < this.kinds.length; i++) {
                const kind = this.kinds[i];
                const selected = kind.subcategories.find(
                    (subcategory: any) => `${subcategory.id}` === `${id}`
                );
                if (selected) {
                    return attribute == "category" ? kind.name : selected.name;
                }
            }
            return "";
        }
    },
};

angular
    .module("admin")
    .component(
        AdminCampaignsListComponent.selector,
        AdminCampaignsListComponent
    );
